import { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { postToast } from 'features/common/utils/postFlash';

import { COLUMN_DEFS } from './PunchupCompedTicketsTable/constants';
import { IdParamsType } from 'utils/types';
import { PunchupSendButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupCancelInviteModal } from './PunchupCancelInviteModal';
import { PunchupColumnPicker } from 'features/common/components/Punchup/composites/tables/PunchupColumnPicker';
import { PunchupCompedTicketsTable } from './PunchupCompedTicketsTable';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { isCancellableComp } from './PunchupCompedTicketsTable/statusColors';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { ROUTES } from 'features/routes';
import { useColumnSelection } from 'features/common/hooks/useColumnSelection';
import { useDebounce } from 'utils/utils';
import { useCompedTicketsPage } from './hooks/useCompedTicketsPage';
import { useTranslation } from 'react-i18next';

export const PunchupCompedTickets = () => {
	const { t } = useTranslation();
	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();

	const {
		setSearchText,
		searchText,
		compedTicketsQueryObject,
		isError,
		showCancelModal,
		closeCancelModal,
		openCancelModal,
		onCancelInvite,
		downloadCompedQuery,
		refetch,
	} = useCompedTicketsPage();

	useEffect(() => {
		if (isError) postToast('error', t('comped-tickets-load-error', 'Failed to load comped tickets'));
	}, [isError]);

	// On a failed FIRST load react-query leaves `data` undefined, so the table body would otherwise spin
	// forever — surface a persistent, retryable error panel instead. A background-refetch error (data
	// already present) keeps the stale table + the toast above, rather than blowing the table away.
	const errorFallback =
		isError && !compedTicketsQueryObject.data ? (
			<PunchupErrorState
				title={t('comped-load-error-title', "We couldn't load comped tickets")}
				description={t('comped-load-error-description', "Something went wrong fetching this show's comped tickets.")}
				onRetry={refetch}
				retryLabel={t('button-try-again', 'Try again')}
			/>
		) : undefined;

	const hasActiveQuery = searchText !== '';

	// Drop the actions column when no row is cancellable so the table shows no
	// empty trailing column — see PunchupCompedTicketsTable/CLAUDE.md "Sticky Options column".
	const hasCancellableRows = useMemo(
		() =>
			compedTicketsQueryObject.data?.pages?.some((page) =>
				page?.data?.some((row) => isCancellableComp(row.transaction_type))
			) ?? false,
		[compedTicketsQueryObject.data]
	);

	const { visibleDefs, visibleKeys, gridColumns, toggleColumn, allDefs, setColumnWidth } = useColumnSelection(
		COLUMN_DEFS,
		hasCancellableRows
	);

	const searchDebounce = useDebounce(setSearchText);

	const onSendTicket = () => push(ROUTES.SEND_TICKETS.replace(':id', id));

	return (
		<PunchupTablePage
			pageName="Comped Tickets"
			header={
				<PunchupTablePageHeader
					title="Comped Tickets"
					description="Send and manage complimentary tickets for your guests."
					customAction={
						<PunchupSendButton onClick={onSendTicket}>{t('comped-tickets-send-tickets')}</PunchupSendButton>
					}
					onDownload={() => downloadCompedQuery.mutate()}
					downloadLabel={t('button-download', 'Download')}
					isDownloading={downloadCompedQuery.isLoading}
					onRefresh={refetch}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={compedTicketsQueryObject.isFetching}
				/>
			}
			search={
				<PunchupSearch onChange={(e) => searchDebounce(e.target.value)} placeholder={t('search-email-or-name')} />
			}
			controlsActions={<PunchupColumnPicker allDefs={allDefs} visibleKeys={visibleKeys} onToggle={toggleColumn} />}
			overlays={
				showCancelModal.show && (
					<PunchupCancelInviteModal
						showModal={showCancelModal.show}
						closeModal={closeCancelModal}
						modalInfo={{
							modalTitle: t('comped-ticket-cancel-invite-modal-title'),
							modalMessage: t('comped-ticket-cancel-invite-modal-description'),
							onDelete: onCancelInvite,
						}}
					/>
				)
			}
			pageFallback={errorFallback}
		>
			<PunchupCompedTicketsTable
				compedTicketsQueryObject={compedTicketsQueryObject}
				openCancelModal={openCancelModal}
				visibleDefs={visibleDefs}
				gridColumns={gridColumns}
				onResizeColumn={setColumnWidth}
				hasActionsColumn={hasCancellableRows}
				hasActiveQuery={hasActiveQuery}
				isLoading={compedTicketsQueryObject.isLoading}
				isFetchingNextPage={compedTicketsQueryObject.isFetchingNextPage}
				isRefetching={isBackgroundRefetch(compedTicketsQueryObject)}
			/>
		</PunchupTablePage>
	);
};
